// WB6 — human-readable labels for the things the Streams page lists.
//
// The page is read mid-incident, and every identifier on it used to be a raw one: a canonical tool
// selector (`research__voice__say`), an instance id (`rinst_a3f9c1e0…`), a stream id. Those are
// exact and unreadable, and an operator scanning a table for "the call that went quiet" reads NAMES,
// not hashes.
//
// The rule here is deliberately narrow: a label is DERIVED from metadata the gateway already sends
// (the route's name/description, the canonical selector's service+tool segments, a stream's declared
// channel or media type) — nothing is invented. When there is no name to derive, the id is TRUNCATED
// and carries the full value in a `title` tooltip, so the exact string is always one hover (and one
// copy) away. An id that is silently shortened with no way back is worse than the long one.

/** A label plus the tooltip that restores whatever the label dropped. */
export interface Labelled {
  /** What to render. */
  label: string;
  /** `title=` text — always the full, exact value. */
  title: string;
  /** True when `label` is not the whole value (so a caller can style it as abbreviated). */
  truncated: boolean;
}

/** An id, shortened to `keep` characters with an ellipsis, carrying the full id as its tooltip. */
export function idLabel(id: string | null | undefined, keep = 12): Labelled {
  const full = (id ?? '').trim();
  if (!full) return { label: '—', title: '', truncated: false };
  if (full.length <= keep) return { label: full, title: full, truncated: false };
  return { label: `${full.slice(0, keep)}…`, title: full, truncated: true };
}

/** Turn a slug into words: `audio_out` → `Audio out`, `voice-say` → `Voice say`. */
export function titleize(slug: string): string {
  const words = slug
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The parts of a tool selector. A gateway selector is `network__service__tool`; a route hop may
 *  carry the network-relative `service__tool`; anything else is passed through as the tool. */
export function splitTool(selector: string): { network?: string; service?: string; tool: string } {
  const parts = (selector ?? '').split('__').filter((p) => p.length > 0);
  if (parts.length >= 3) return { network: parts[0], service: parts[1], tool: parts.slice(2).join('__') };
  if (parts.length === 2) return { service: parts[0], tool: parts[1] };
  return { tool: selector ?? '' };
}

/** A tool selector as a person reads it: `research__voice__say` → `Voice · say`.
 *
 *  The network segment is dropped on purpose — the page already scopes everything to one selected
 *  network, so repeating it on every row is noise. The exact selector belongs in the tooltip. */
export function toolLabel(selector: string): Labelled {
  const full = (selector ?? '').trim();
  if (!full) return { label: '—', title: '', truncated: false };
  const { service, tool } = splitTool(full);
  if (!service) return { label: full, title: full, truncated: false };
  return { label: `${titleize(service)} · ${tool}`, title: full, truncated: true };
}

/** The route's display name, and the subtitle that explains it.
 *
 *  A route name is chosen by whoever declared the route, so it IS the friendly name; the optional
 *  description is what turns `call` into something a newcomer can act on. */
export function routeLabel(route: { name?: string | null; description?: string | null } | null | undefined): {
  name: string;
  sub: string;
} {
  const name = (route?.name ?? '').trim();
  const sub = (route?.description ?? '').trim();
  return { name: name || '—', sub };
}

/** What a stream is called on screen.
 *
 *  In order of usefulness: the name of its declared channel (`audio_out` → `Audio out`), its media
 *  type, then — with nothing declared — the truncated id. Never the raw full id: that is the `title`,
 *  which is rendered beside the label as the exact handle anyway. */
export function streamLabel(stream: {
  id: string;
  media_type?: string | null;
  channels?: { name?: string | null }[] | null;
}): Labelled {
  const channel = (stream.channels ?? []).map((c) => (c?.name ?? '').trim()).find((n) => n.length > 0);
  if (channel) return { label: titleize(channel), title: stream.id, truncated: true };
  const media = (stream.media_type ?? '').trim();
  if (media) return { label: media, title: stream.id, truncated: true };
  return idLabel(stream.id);
}
